import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Wallet, Users, FileText, Plus, X, Search, Download, Check, Clock,
  AlertCircle, RefreshCw, ChevronRight, ChevronLeft, Trash2, Edit2, Lock,
  Ban, CreditCard, Calendar, Building2, TrendingUp, Loader2,
  IndianRupee, HandCoins, CheckCircle2,
} from 'lucide-react';
import { jsPDF } from 'jspdf';

import './style/Payroll.css';
import { host } from './script/variables';

/* ═══════════════════════════════════════════════════════════════════════════
   API layer — every route answers { ok, data, message?, ... }
   ═════════════════════════════════════════════════════════════════════════ */

const BASE = '/payrollapi';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: localStorage.getItem('partnertoken') || '',
});

class ApiError extends Error {
  constructor(message, payload = {}, status = 0) {
    super(message);
    this.payload = payload;
    this.status = status;
  }
}

async function api(path, { method = 'GET', body, signal } = {}) {
  let res;
  try {
    res = await fetch(`${host}${BASE}${path}`, {
      method,
      headers: authHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError('Could not reach the server. Check your connection.');
  }

  let payload = {};
  try { payload = await res.json(); } catch { /* non-JSON body */ }

  if (!res.ok || payload.ok === false) {
    throw new ApiError(payload.message || `Request failed (${res.status})`, payload, res.status);
  }
  return payload;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Constants & helpers
   ═════════════════════════════════════════════════════════════════════════ */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CURRENCY_SYMBOL = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ' };

const fmtMoney = (n, cur = 'INR') =>
  `${CURRENCY_SYMBOL[cur] || ''}${(Number(n) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const RUN_STATUS = {
  draft:     { label: 'Draft',     color: '#C79A3B', icon: Edit2 },
  finalized: { label: 'Finalized', color: '#24455C', icon: Lock },
  paid:      { label: 'Paid',      color: '#2E7D52', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: '#8a8f98', icon: Ban },
};

const PAY_STATUS = {
  pending: { label: 'Pending', color: '#C79A3B' },
  paid:    { label: 'Paid',    color: '#2E7D52' },
};

const PAY_METHODS = [
  { value: 'cash',   label: 'Cash' },
  { value: 'bank',   label: 'Bank transfer' },
  { value: 'upi',    label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other',  label: 'Other' },
];

const employeeName = (snap = {}) =>
  `${snap.firstName || ''} ${snap.lastName || ''}`.trim() || '—';

const todayISO = () => new Date().toISOString().slice(0, 10);

const genYears = () => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3];
};

/* ═══════════════════════════════════════════════════════════════════════════
   Payslip PDF — pure jsPDF canvas, no autoTable (matches GenerateInvoice.js)
   ═════════════════════════════════════════════════════════════════════════ */

function downloadPayslipPDF(payslip, run) {
  const cur = payslip.currency || 'INR';
  const money = n => fmtMoney(n, cur);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;
  let y = 56;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Payslip', M, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(payslip.slipNumber || '', W - M, y, { align: 'right' });
  doc.setTextColor(0);
  y += 10;
  doc.setDrawColor(220);
  doc.line(M, y, W - M, y);
  y += 24;

  const period = `${MONTHS[(payslip.periodMonth || 1) - 1]} ${payslip.periodYear || ''}`;
  const outlet = run?.cafeId?.name || 'All outlets';

  doc.setFontSize(10);
  const left = [
    ['Employee', employeeName(payslip.employeeSnapshot)],
    ['Code', payslip.employeeSnapshot?.employeeCode || '—'],
    ['Designation', (payslip.employeeSnapshot?.designations || []).join(', ') || '—'],
  ];
  const right = [
    ['Pay period', period],
    ['Outlet', outlet],
    ['Status', (payslip.payment?.status || 'pending').toUpperCase()],
  ];
  left.forEach((row, i) => {
    doc.setTextColor(130); doc.text(row[0], M, y + i * 16);
    doc.setTextColor(20);  doc.text(String(row[1]), M + 90, y + i * 16);
  });
  right.forEach((row, i) => {
    doc.setTextColor(130); doc.text(row[0], W / 2 + 10, y + i * 16);
    doc.setTextColor(20);  doc.text(String(row[1]), W / 2 + 90, y + i * 16);
  });
  y += 3 * 16 + 20;

  const section = (title, rows, totalLabel, totalVal) => {
    doc.setFillColor(244, 247, 249);
    doc.rect(M, y, W - 2 * M, 22, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(30);
    doc.text(title, M + 10, y + 15);
    doc.text(money(totalVal), W - M - 10, y + 15, { align: 'right' });
    y += 30;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(60);
    (rows.length ? rows : [{ label: '—', amount: 0 }]).forEach(r => {
      doc.text(String(r.label || ''), M + 10, y);
      doc.text(money(r.amount), W - M - 10, y, { align: 'right' });
      y += 16;
    });
    y += 10;
    void totalLabel;
  };

  section('Earnings', payslip.earnings || [], 'Gross', payslip.gross);
  section('Deductions', payslip.deductions || [], 'Total deductions', payslip.totalDeductions);

  doc.setDrawColor(220); doc.line(M, y, W - M, y); y += 20;
  doc.setFillColor(36, 69, 92);
  doc.rect(M, y - 16, W - 2 * M, 30, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(255);
  doc.text('Net Pay', M + 10, y + 4);
  doc.text(money(payslip.net), W - M - 10, y + 4, { align: 'right' });
  doc.setTextColor(0);
  y += 40;

  if (payslip.payment?.status === 'paid') {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120);
    const when = payslip.payment.paidAt ? new Date(payslip.payment.paidAt).toLocaleDateString() : '';
    const via = (PAY_METHODS.find(m => m.value === payslip.payment.method) || {}).label || payslip.payment.method;
    doc.text(`Paid on ${when} via ${via}${payslip.payment.reference ? ` · Ref: ${payslip.payment.reference}` : ''}`, M, y);
  }

  doc.setFontSize(8); doc.setTextColor(170);
  doc.text('Generated by RestroPilots · This is a system-generated payslip.', M, doc.internal.pageSize.getHeight() - 36);

  const safe = (employeeName(payslip.employeeSnapshot) || 'employee').replace(/\s+/g, '_');
  doc.save(`Payslip_${safe}_${period.replace(/\s+/g, '_')}.pdf`);
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tiny UI primitives
   ═════════════════════════════════════════════════════════════════════════ */

const StatusBadge = ({ map, value }) => {
  const cfg = map[value] || { label: value, color: '#8a8f98' };
  return (
    <span className="pr-badge" style={{ color: cfg.color, background: `${cfg.color}1a` }}>
      {cfg.icon ? <cfg.icon size={13} /> : null}
      {cfg.label}
    </span>
  );
};

const StatTile = ({ icon: Icon, label, value, sub, tone }) => (
  <div className={`pr-stat ${tone ? `pr-stat--${tone}` : ''}`}>
    <div className="pr-stat-icon"><Icon size={18} /></div>
    <div className="pr-stat-body">
      <span className="pr-stat-value">{value}</span>
      <span className="pr-stat-label">{label}</span>
      {sub ? <span className="pr-stat-sub">{sub}</span> : null}
    </div>
  </div>
);

const Modal = ({ title, onClose, children, footer, wide }) => (
  <div className="pr-modal-scrim" onMouseDown={onClose}>
    <div
      className={`pr-modal ${wide ? 'pr-modal--wide' : ''}`}
      role="dialog"
      aria-modal="true"
      onMouseDown={e => e.stopPropagation()}
    >
      <header className="pr-modal-head">
        <h3>{title}</h3>
        <button className="pr-icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
      </header>
      <div className="pr-modal-body">{children}</div>
      {footer ? <footer className="pr-modal-foot">{footer}</footer> : null}
    </div>
  </div>
);

const Empty = ({ icon: Icon, title, hint, action }) => (
  <div className="pr-empty">
    <div className="pr-empty-icon"><Icon size={26} /></div>
    <p className="pr-empty-title">{title}</p>
    {hint ? <p className="pr-empty-hint">{hint}</p> : null}
    {action}
  </div>
);

const Spinner = ({ label }) => (
  <div className="pr-loading"><Loader2 size={20} className="pr-spin" /> {label || 'Loading…'}</div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Line-item editor (earnings / deductions)
   ═════════════════════════════════════════════════════════════════════════ */

const LineEditor = ({ title, rows, onChange, locked }) => {
  const set = (i, key, val) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
    onChange(next);
  };
  const add = () => onChange([...rows, { label: '', amount: 0, kind: '' }]);
  const remove = i => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="pr-lines">
      <div className="pr-lines-head">
        <span>{title}</span>
        {!locked && (
          <button type="button" className="pr-link-btn" onClick={add}><Plus size={14} /> Add</button>
        )}
      </div>
      {rows.length === 0 && <p className="pr-lines-empty">No {title.toLowerCase()}.</p>}
      {rows.map((r, i) => {
        const isAdvance = r.kind === 'advance';
        return (
          <div className="pr-line-row" key={i}>
            <input
              className="pr-input"
              placeholder="Label"
              value={r.label}
              disabled={locked || isAdvance}
              onChange={e => set(i, 'label', e.target.value)}
            />
            <input
              className="pr-input pr-input--amt"
              type="number" min="0" step="0.01"
              value={r.amount}
              disabled={locked || isAdvance}
              onChange={e => set(i, 'amount', e.target.value)}
            />
            {!locked && !isAdvance ? (
              <button type="button" className="pr-icon-btn pr-icon-btn--danger" onClick={() => remove(i)} aria-label="Remove">
                <Trash2 size={15} />
              </button>
            ) : <span className="pr-line-lock" title={isAdvance ? 'Managed automatically' : ''}>{isAdvance ? <HandCoins size={14} /> : ''}</span>}
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Main component
   ═════════════════════════════════════════════════════════════════════════ */

const TABS = [
  { key: 'overview', label: 'Overview', Icon: TrendingUp },
  { key: 'runs',     label: 'Payroll Runs', Icon: Wallet },
  { key: 'history',  label: 'Payslips', Icon: FileText },
  { key: 'advances', label: 'Advances', Icon: HandCoins },
];

const Payroll = ({ properties = [] }) => {
  const [tab, setTab] = useState('overview');
  const [notice, setNotice] = useState(null); // { type:'error'|'success', text }
  const noticeTimer = useRef(null);

  const flash = useCallback((type, text) => {
    setNotice({ type, text });
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 4000);
  }, []);

  const outletName = useCallback(
    id => (id ? (properties.find(p => String(p._id) === String(id))?.name || 'Outlet') : 'All outlets'),
    [properties]
  );

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  return (
    <div className="pr-root">
      <header className="pr-header">
        <div>
          <h1 className="pr-title"><IndianRupee size={22} /> Payroll</h1>
          <p className="pr-subtitle">Run payroll, record payouts and track advances across your outlets.</p>
        </div>
      </header>

      <nav className="pr-tabbar" aria-label="Payroll sections">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`pr-tab ${tab === t.key ? 'pr-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
            aria-current={tab === t.key ? 'page' : undefined}
          >
            <t.Icon size={17} /> {t.label}
          </button>
        ))}
      </nav>

      {notice && (
        <div className={`pr-notice pr-notice--${notice.type}`} role="status">
          {notice.type === 'error' ? <AlertCircle size={16} /> : <Check size={16} />}
          <span>{notice.text}</span>
          <button className="pr-icon-btn" onClick={() => setNotice(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="pr-content">
        {tab === 'overview' && <OverviewTab flash={flash} goRuns={() => setTab('runs')} />}
        {tab === 'runs'     && <RunsTab flash={flash} properties={properties} outletName={outletName} />}
        {tab === 'history'  && <HistoryTab flash={flash} />}
        {tab === 'advances' && <AdvancesTab flash={flash} />}
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────────────── Overview ── */

const OverviewTab = ({ flash, goRuns }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api(`/summary?year=${year}&month=${month}`);
      setData(data);
    } catch (e) { flash('error', e.message); }
    finally { setLoading(false); }
  }, [flash, month, year]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Spinner />;
  if (!data) return null;

  const tp = data.thisPeriod;
  return (
    <div className="pr-overview">
      <div className="pr-period-picker">
        <Calendar size={16} />
        <select className="pr-select" value={month} onChange={e => setMonth(+e.target.value)}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select className="pr-select" value={year} onChange={e => setYear(+e.target.value)}>
          {genYears().map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="pr-icon-btn" onClick={load} aria-label="Refresh"><RefreshCw size={16} /></button>
      </div>

      <div className="pr-stat-grid">
        <StatTile icon={Wallet} label="Net payroll (this period)" value={fmtMoney(tp.net)} sub={`${tp.slips} payslip${tp.slips === 1 ? '' : 's'}`} />
        <StatTile icon={CheckCircle2} label="Paid out" value={fmtMoney(tp.paid)} sub={`${tp.paidCount}/${tp.slips} settled`} tone="success" />
        <StatTile icon={Clock} label="Pending" value={fmtMoney(tp.pending)} tone="warning" />
        <StatTile icon={Users} label="Active employees" value={data.activeEmployees} />
        <StatTile icon={HandCoins} label="Advances outstanding" value={fmtMoney(data.advancesOutstanding.amount)} sub={`${data.advancesOutstanding.count} open`} />
        <StatTile icon={Edit2} label="Draft runs" value={data.draftRuns} sub="awaiting finalize" />
      </div>

      <div className="pr-cta-card">
        <div>
          <h3>Ready to pay your team?</h3>
          <p>Generate a payroll run for a period and we'll build a payslip for every active employee.</p>
        </div>
        <button className="pr-btn pr-btn--primary" onClick={goRuns}><Plus size={16} /> Go to payroll runs</button>
      </div>
    </div>
  );
};

/* ───────────────────────────────────────────────────────────────── Runs ── */

const RunsTab = ({ flash, properties, outletName }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openRunId, setOpenRunId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api('/runs?limit=50');
      setRuns(data);
    } catch (e) { flash('error', e.message); }
    finally { setLoading(false); }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  if (openRunId) {
    return (
      <RunDetail
        runId={openRunId}
        flash={flash}
        outletName={outletName}
        onBack={() => { setOpenRunId(null); load(); }}
      />
    );
  }

  return (
    <div>
      <div className="pr-toolbar">
        <h2 className="pr-section-title">Payroll runs</h2>
        <button className="pr-btn pr-btn--primary" onClick={() => setCreating(true)}><Plus size={16} /> New run</button>
      </div>

      {loading ? <Spinner /> : runs.length === 0 ? (
        <Empty
          icon={Wallet}
          title="No payroll runs yet"
          hint="Create your first run to generate payslips for your team."
          action={<button className="pr-btn pr-btn--primary" onClick={() => setCreating(true)}><Plus size={16} /> New run</button>}
        />
      ) : (
        <div className="pr-run-list">
          {runs.map(r => {
            const paidPct = r.totals.net > 0 ? Math.round((r.totals.paid / r.totals.net) * 100) : 0;
            return (
              <button key={r._id} className="pr-run-card" onClick={() => setOpenRunId(r._id)}>
                <div className="pr-run-card-main">
                  <div className="pr-run-card-top">
                    <span className="pr-run-period">{MONTHS[r.periodMonth - 1]} {r.periodYear}</span>
                    <StatusBadge map={RUN_STATUS} value={r.status} />
                  </div>
                  <div className="pr-run-meta">
                    <span><Building2 size={13} /> {r.cafeId?.name || outletName(r.cafeId)}</span>
                    <span><Users size={13} /> {r.totals.employees}</span>
                    <span className="pr-run-num">{r.runNumber}</span>
                  </div>
                </div>
                <div className="pr-run-card-money">
                  <span className="pr-run-net">{fmtMoney(r.totals.net, r.currency)}</span>
                  <span className="pr-run-paid">{paidPct}% paid</span>
                </div>
                <ChevronRight size={18} className="pr-run-chevron" />
              </button>
            );
          })}
        </div>
      )}

      {creating && (
        <CreateRunModal
          properties={properties}
          flash={flash}
          onClose={() => setCreating(false)}
          onCreated={id => { setCreating(false); load(); setOpenRunId(id); }}
        />
      )}
    </div>
  );
};

const CreateRunModal = ({ properties, flash, onClose, onCreated }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [cafeId, setCafeId] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api('/runs', {
        method: 'POST',
        body: { periodMonth: month, periodYear: year, cafeId: cafeId || null, currency },
      });
      flash('success', `Run ${data.run.runNumber} created with ${data.payslips.length} payslip(s).`);
      onCreated(data.run._id);
    } catch (e) {
      flash('error', e.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New payroll run"
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="pr-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="pr-btn pr-btn--primary" onClick={submit} disabled={busy}>
            {busy ? <><Loader2 size={15} className="pr-spin" /> Generating…</> : <>Generate payslips</>}
          </button>
        </>
      }
    >
      <div className="pr-form-grid">
        <label className="pr-field">
          <span>Month</span>
          <select className="pr-select" value={month} onChange={e => setMonth(+e.target.value)}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label className="pr-field">
          <span>Year</span>
          <select className="pr-select" value={year} onChange={e => setYear(+e.target.value)}>
            {genYears().map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="pr-field pr-field--full">
          <span>Outlet</span>
          <select className="pr-select" value={cafeId} onChange={e => setCafeId(e.target.value)}>
            <option value="">All outlets</option>
            {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        </label>
        <label className="pr-field">
          <span>Currency</span>
          <select className="pr-select" value={currency} onChange={e => setCurrency(e.target.value)}>
            {Object.keys(CURRENCY_SYMBOL).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <p className="pr-hint">
        A payslip is created for every <strong>active</strong> employee{cafeId ? ' assigned to this outlet' : ''}, seeded
        with their basic salary. Outstanding advances are suggested as deductions. You can adjust each payslip before finalizing.
      </p>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────── Run detail ── */

const RunDetail = ({ runId, flash, outletName, onBack }) => {
  const [run, setRun] = useState(null);
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editSlip, setEditSlip] = useState(null);
  const [paySlip, setPaySlip] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api(`/runs/${runId}`);
      setRun(data.run);
      setSlips(data.payslips);
    } catch (e) { flash('error', e.message); }
    finally { setLoading(false); }
  }, [runId, flash]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) flash('success', okMsg); await load(); }
    catch (e) { flash('error', e.message); }
    finally { setBusy(false); }
  };

  if (loading && !run) return <Spinner />;
  if (!run) return null;

  const isDraft = run.status === 'draft';
  const canPay = run.status === 'finalized' || run.status === 'paid';

  const finalize = () => act(() => api(`/runs/${runId}/finalize`, { method: 'PATCH' }), 'Run finalized.');
  const cancel = () => {
    if (!window.confirm('Cancel this run? Payslips stay recorded but it can no longer be paid.')) return;
    act(() => api(`/runs/${runId}/cancel`, { method: 'PATCH' }), 'Run cancelled.');
  };
  const del = () => {
    if (!window.confirm('Delete this draft run and all its payslips? This cannot be undone.')) return;
    act(async () => { await api(`/runs/${runId}`, { method: 'DELETE' }); onBack(); });
  };

  return (
    <div className="pr-detail">
      <div className="pr-detail-head">
        <button className="pr-btn pr-btn--ghost" onClick={onBack}><ChevronLeft size={16} /> Back</button>
        <div className="pr-detail-actions">
          {isDraft && <button className="pr-btn" onClick={del} disabled={busy}><Trash2 size={15} /> Delete</button>}
          {isDraft && <button className="pr-btn pr-btn--primary" onClick={finalize} disabled={busy}><Lock size={15} /> Finalize</button>}
          {run.status === 'finalized' && <button className="pr-btn" onClick={cancel} disabled={busy}><Ban size={15} /> Cancel</button>}
        </div>
      </div>

      <div className="pr-detail-summary">
        <div>
          <h2 className="pr-detail-title">{MONTHS[run.periodMonth - 1]} {run.periodYear}</h2>
          <div className="pr-detail-meta">
            <StatusBadge map={RUN_STATUS} value={run.status} />
            <span><Building2 size={13} /> {run.cafeId?.name || outletName(run.cafeId)}</span>
            <span className="pr-run-num">{run.runNumber}</span>
          </div>
        </div>
        <div className="pr-detail-totals">
          <div><span className="pr-t-label">Gross</span><span className="pr-t-val">{fmtMoney(run.totals.gross, run.currency)}</span></div>
          <div><span className="pr-t-label">Deductions</span><span className="pr-t-val">{fmtMoney(run.totals.deductions, run.currency)}</span></div>
          <div><span className="pr-t-label">Net</span><span className="pr-t-val pr-t-val--strong">{fmtMoney(run.totals.net, run.currency)}</span></div>
          <div><span className="pr-t-label">Paid</span><span className="pr-t-val pr-t-val--success">{fmtMoney(run.totals.paid, run.currency)}</span></div>
        </div>
      </div>

      <div className="pr-table-wrap">
        <table className="pr-table">
          <thead>
            <tr>
              <th>Employee</th><th className="pr-num">Gross</th><th className="pr-num">Deductions</th>
              <th className="pr-num">Net</th><th>Payment</th><th className="pr-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {slips.map(s => (
              <tr key={s._id}>
                <td>
                  <div className="pr-emp-cell">
                    <span className="pr-emp-name">{employeeName(s.employeeSnapshot)}</span>
                    <span className="pr-emp-code">{s.employeeSnapshot?.employeeCode || s.slipNumber}</span>
                  </div>
                </td>
                <td className="pr-num">{fmtMoney(s.gross, s.currency)}</td>
                <td className="pr-num">{fmtMoney(s.totalDeductions, s.currency)}</td>
                <td className="pr-num pr-num--strong">{fmtMoney(s.net, s.currency)}</td>
                <td><StatusBadge map={PAY_STATUS} value={s.payment?.status || 'pending'} /></td>
                <td className="pr-actions-col">
                  <div className="pr-row-actions">
                    {isDraft && (
                      <button className="pr-icon-btn" title="Edit" onClick={() => setEditSlip(s)}><Edit2 size={15} /></button>
                    )}
                    {canPay && s.payment?.status !== 'paid' && (
                      <button className="pr-icon-btn pr-icon-btn--accent" title="Mark paid" onClick={() => setPaySlip(s)}><CreditCard size={15} /></button>
                    )}
                    {canPay && s.payment?.status === 'paid' && (
                      <button className="pr-icon-btn" title="Revert payment" onClick={() => act(() => api(`/payslips/${s._id}/unpay`, { method: 'PATCH' }), 'Payment reverted.')}><RefreshCw size={15} /></button>
                    )}
                    <button className="pr-icon-btn" title="Download PDF" onClick={() => downloadPayslipPDF(s, run)}><Download size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editSlip && (
        <EditSlipModal
          slip={editSlip}
          flash={flash}
          onClose={() => setEditSlip(null)}
          onSaved={() => { setEditSlip(null); load(); }}
        />
      )}
      {paySlip && (
        <PayModal
          slip={paySlip}
          flash={flash}
          onClose={() => setPaySlip(null)}
          onPaid={() => { setPaySlip(null); load(); }}
        />
      )}
    </div>
  );
};

const EditSlipModal = ({ slip, flash, onClose, onSaved }) => {
  const [earnings, setEarnings] = useState(slip.earnings || []);
  const [deductions, setDeductions] = useState(slip.deductions || []);
  const [notes, setNotes] = useState(slip.notes || '');
  const [busy, setBusy] = useState(false);

  const num = rows => rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const gross = num(earnings);
  const ded = num(deductions);
  const net = Math.max(0, gross - ded);

  const save = async () => {
    setBusy(true);
    try {
      await api(`/payslips/${slip._id}`, {
        method: 'PUT',
        body: {
          earnings: earnings.map(r => ({ label: r.label, amount: Number(r.amount) || 0, kind: r.kind || '' })),
          deductions: deductions.filter(r => r.kind !== 'advance').map(r => ({ label: r.label, amount: Number(r.amount) || 0, kind: r.kind || '' })),
          notes,
        },
      });
      flash('success', 'Payslip updated.');
      onSaved();
    } catch (e) { flash('error', e.message); setBusy(false); }
  };

  return (
    <Modal
      wide
      title={`Edit payslip — ${employeeName(slip.employeeSnapshot)}`}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <div className="pr-foot-net">Net: <strong>{fmtMoney(net, slip.currency)}</strong></div>
          <button className="pr-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="pr-btn pr-btn--primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <LineEditor title="Earnings" rows={earnings} onChange={setEarnings} />
      <LineEditor title="Deductions" rows={deductions} onChange={setDeductions} />
      <p className="pr-hint">Advance-recovery lines are managed automatically and recalculated when you save.</p>
      <label className="pr-field pr-field--full">
        <span>Notes</span>
        <textarea className="pr-input" rows={2} value={notes} maxLength={1000} onChange={e => setNotes(e.target.value)} />
      </label>
    </Modal>
  );
};

const PayModal = ({ slip, flash, onClose, onPaid }) => {
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState(todayISO());
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    setBusy(true);
    try {
      await api(`/payslips/${slip._id}/pay`, { method: 'PATCH', body: { method, reference, paidAt } });
      flash('success', `Marked ${employeeName(slip.employeeSnapshot)} as paid.`);
      onPaid();
    } catch (e) { flash('error', e.message); setBusy(false); }
  };

  return (
    <Modal
      title={`Record payment — ${employeeName(slip.employeeSnapshot)}`}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="pr-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="pr-btn pr-btn--primary" onClick={pay} disabled={busy}>{busy ? 'Saving…' : `Pay ${fmtMoney(slip.net, slip.currency)}`}</button>
        </>
      }
    >
      <div className="pr-form-grid">
        <label className="pr-field">
          <span>Method</span>
          <select className="pr-select" value={method} onChange={e => setMethod(e.target.value)}>
            {PAY_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        <label className="pr-field">
          <span>Date</span>
          <input className="pr-input" type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)} />
        </label>
        <label className="pr-field pr-field--full">
          <span>Reference (optional)</span>
          <input className="pr-input" value={reference} maxLength={120} placeholder="UTR / cheque no. / note" onChange={e => setReference(e.target.value)} />
        </label>
      </div>
    </Modal>
  );
};

/* ──────────────────────────────────────────────────────────── Payslips ── */

const HistoryTab = ({ flash }) => {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [year, setYear] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ pages: 1, total: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), limit: '50' });
      if (search) qs.set('search', search);
      if (status) qs.set('status', status);
      if (year) qs.set('year', year);
      const res = await api(`/payslips?${qs.toString()}`);
      setSlips(res.data);
      setMeta({ pages: res.pages || 1, total: res.total || 0 });
    } catch (e) { flash('error', e.message); }
    finally { setLoading(false); }
  }, [flash, page, search, status, year]);

  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  const openPdf = async s => {
    try {
      const { data } = await api(`/payslips/${s._id}`);
      downloadPayslipPDF(data.payslip, data.run);
    } catch (e) { flash('error', e.message); }
  };

  return (
    <div>
      <div className="pr-toolbar pr-toolbar--filters">
        <div className="pr-search">
          <Search size={16} />
          <input className="pr-input" placeholder="Search name, code or slip no." value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} />
        </div>
        <select className="pr-select" value={status} onChange={e => { setPage(1); setStatus(e.target.value); }}>
          <option value="">All payments</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
        <select className="pr-select" value={year} onChange={e => { setPage(1); setYear(e.target.value); }}>
          <option value="">All years</option>
          {genYears().map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? <Spinner /> : slips.length === 0 ? (
        <Empty icon={FileText} title="No payslips found" hint="Payslips appear here once you create a payroll run." />
      ) : (
        <>
          <div className="pr-table-wrap">
            <table className="pr-table">
              <thead>
                <tr>
                  <th>Employee</th><th>Period</th><th className="pr-num">Net</th><th>Payment</th><th className="pr-actions-col">PDF</th>
                </tr>
              </thead>
              <tbody>
                {slips.map(s => (
                  <tr key={s._id}>
                    <td>
                      <div className="pr-emp-cell">
                        <span className="pr-emp-name">{employeeName(s.employeeSnapshot)}</span>
                        <span className="pr-emp-code">{s.slipNumber}</span>
                      </div>
                    </td>
                    <td>{MONTHS[s.periodMonth - 1]?.slice(0, 3)} {s.periodYear}</td>
                    <td className="pr-num pr-num--strong">{fmtMoney(s.net, s.currency)}</td>
                    <td><StatusBadge map={PAY_STATUS} value={s.payment?.status || 'pending'} /></td>
                    <td className="pr-actions-col">
                      <button className="pr-icon-btn" title="Download PDF" onClick={() => openPdf(s)}><Download size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {meta.pages > 1 && (
            <div className="pr-pager">
              <button className="pr-btn pr-btn--ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={15} /> Prev</button>
              <span>Page {page} of {meta.pages} · {meta.total} payslips</span>
              <button className="pr-btn pr-btn--ghost" disabled={page >= meta.pages} onClick={() => setPage(p => p + 1)}>Next <ChevronRight size={15} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────── Advances ── */

const AdvancesTab = ({ flash }) => {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = status ? `?status=${status}` : '';
      const { data } = await api(`/advances${qs}`);
      setAdvances(data);
    } catch (e) { flash('error', e.message); }
    finally { setLoading(false); }
  }, [flash, status]);

  useEffect(() => { load(); }, [load]);

  const cancel = async a => {
    if (!window.confirm('Cancel the remaining balance of this advance?')) return;
    try { await api(`/advances/${a._id}/cancel`, { method: 'PATCH' }); flash('success', 'Advance cancelled.'); load(); }
    catch (e) { flash('error', e.message); }
  };

  const ADV_STATUS = {
    outstanding: { label: 'Outstanding', color: '#C79A3B' },
    recovering:  { label: 'Recovering', color: '#24455C' },
    cleared:     { label: 'Cleared', color: '#2E7D52' },
    cancelled:   { label: 'Cancelled', color: '#8a8f98' },
  };

  return (
    <div>
      <div className="pr-toolbar">
        <div className="pr-toolbar-left">
          <h2 className="pr-section-title">Advances & loans</h2>
          <select className="pr-select" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="outstanding">Outstanding</option>
            <option value="recovering">Recovering</option>
            <option value="cleared">Cleared</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <button className="pr-btn pr-btn--primary" onClick={() => setCreating(true)}><Plus size={16} /> New advance</button>
      </div>

      {loading ? <Spinner /> : advances.length === 0 ? (
        <Empty icon={HandCoins} title="No advances recorded" hint="Record a salary advance and it'll be recovered automatically in future runs." />
      ) : (
        <div className="pr-table-wrap">
          <table className="pr-table">
            <thead>
              <tr>
                <th>Employee</th><th className="pr-num">Amount</th><th className="pr-num">Outstanding</th>
                <th>Reason</th><th>Status</th><th className="pr-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {advances.map(a => (
                <tr key={a._id}>
                  <td>
                    <div className="pr-emp-cell">
                      <span className="pr-emp-name">{employeeName(a.employeeSnapshot)}</span>
                      <span className="pr-emp-code">{a.employeeSnapshot?.employeeCode || ''}</span>
                    </div>
                  </td>
                  <td className="pr-num">{fmtMoney(a.amount, a.currency)}</td>
                  <td className="pr-num pr-num--strong">{fmtMoney(a.outstanding, a.currency)}</td>
                  <td className="pr-reason">{a.reason || '—'}</td>
                  <td><StatusBadge map={ADV_STATUS} value={a.status} /></td>
                  <td className="pr-actions-col">
                    {a.status !== 'cancelled' && a.status !== 'cleared' && (
                      <button className="pr-icon-btn pr-icon-btn--danger" title="Cancel balance" onClick={() => cancel(a)}><Ban size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateAdvanceModal
          flash={flash}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
};

const CreateAdvanceModal = ({ flash, onClose, onCreated }) => {
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [amount, setAmount] = useState('');
  const [perRunRecovery, setPerRun] = useState('');
  const [reason, setReason] = useState('');
  const [issuedAt, setIssuedAt] = useState(todayISO());
  const [busy, setBusy] = useState(false);
  const [loadingEmp, setLoadingEmp] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingEmp(true);
      try {
        const qs = empSearch ? `?search=${encodeURIComponent(empSearch)}` : '';
        const { data } = await api(`/employees${qs}`);
        if (active) setEmployees(data);
      } catch (e) { flash('error', e.message); }
      finally { if (active) setLoadingEmp(false); }
    })();
    return () => { active = false; };
  }, [empSearch, flash]);

  const submit = async () => {
    if (!employeeId) { flash('error', 'Pick an employee.'); return; }
    if (!(Number(amount) > 0)) { flash('error', 'Enter an amount greater than zero.'); return; }
    setBusy(true);
    try {
      await api('/advances', {
        method: 'POST',
        body: {
          employeeId,
          amount: Number(amount),
          perRunRecovery: perRunRecovery ? Number(perRunRecovery) : 0,
          reason,
          issuedAt,
        },
      });
      flash('success', 'Advance recorded.');
      onCreated();
    } catch (e) { flash('error', e.message); setBusy(false); }
  };

  return (
    <Modal
      title="New advance"
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="pr-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="pr-btn pr-btn--primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Record advance'}</button>
        </>
      }
    >
      <label className="pr-field pr-field--full">
        <span>Find employee</span>
        <div className="pr-search pr-search--plain">
          <Search size={15} />
          <input className="pr-input" placeholder="Search by name or code" value={empSearch} onChange={e => setEmpSearch(e.target.value)} />
        </div>
      </label>
      <label className="pr-field pr-field--full">
        <span>Employee</span>
        <select className="pr-select" value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
          <option value="">{loadingEmp ? 'Loading…' : 'Select an employee'}</option>
          {employees.map(e => (
            <option key={e._id} value={e._id}>
              {`${e.firstName || ''} ${e.lastName || ''}`.trim()}{e.employeeCode ? ` (${e.employeeCode})` : ''}
            </option>
          ))}
        </select>
      </label>
      <div className="pr-form-grid">
        <label className="pr-field">
          <span>Amount</span>
          <input className="pr-input" type="number" min="1" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} />
        </label>
        <label className="pr-field">
          <span>Recover per run (optional)</span>
          <input className="pr-input" type="number" min="0" step="0.01" placeholder="Full" value={perRunRecovery} onChange={e => setPerRun(e.target.value)} />
        </label>
        <label className="pr-field">
          <span>Issued on</span>
          <input className="pr-input" type="date" value={issuedAt} onChange={e => setIssuedAt(e.target.value)} />
        </label>
      </div>
      <label className="pr-field pr-field--full">
        <span>Reason (optional)</span>
        <input className="pr-input" value={reason} maxLength={500} onChange={e => setReason(e.target.value)} />
      </label>
      <p className="pr-hint">Leave “recover per run” blank to recover the full amount from the next payroll run (capped so net pay never goes below zero).</p>
    </Modal>
  );
};

export default Payroll;
